
import { Component, OnInit } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormBuilder, FormGroup, ReactiveFormsModule, Validators } from '@angular/forms';
import { AuthService } from '../../_services/auth.service';
import { User } from '../../_interfaces/user';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatInputModule } from '@angular/material/input';
import { MatButtonModule } from '@angular/material/button';
import { MatIconModule } from '@angular/material/icon';
import { MatSlideToggleModule } from '@angular/material/slide-toggle';
import { MatSnackBar } from '@angular/material/snack-bar';
import { LoadingSpinnerComponent } from '../../shared/components/loading-spinner/loading-spinner.component';
import { ResetPassword } from '../../shared/forms/reset-passwor-form';
import { NewPasswordComponent } from '../../shared/components/password/new-password.component';
import { TranslateModule, TranslateService } from '@ngx-translate/core';

@Component({
  selector: 'app-settings-security',
  imports: [
    CommonModule, 
    ReactiveFormsModule, 
    MatFormFieldModule, 
    MatInputModule, 
    MatButtonModule,
    MatIconModule,
    MatSlideToggleModule,
    LoadingSpinnerComponent,
    NewPasswordComponent,
    TranslateModule
  ],
  templateUrl: './security.component.html',
})
export class SettingsSecurityComponent implements OnInit {
  user: User | null = null;
  passwordForm!: FormGroup;
  resetPassword!: ResetPassword;
  canChangePassword = true;
  showCurrentPassword = false;
  showNewPassword = false;
  showConfirmPassword = false;
  encounterForm: FormGroup;
  isLoadingPassword = false;
  isLoadingEncounter = false;

  get isConsentCentrallyManaged(): boolean {
    return this.user?.entity?.settings?.enableCopilotPatientConsent === true;
  }

  constructor(
    private authService: AuthService,
    private fb: FormBuilder,
    private snackBar: MatSnackBar,
    private translate: TranslateService
  ) {
    this.user = this.authService.getUser();
    this.canChangePassword = this.authService.isPasswordUser();

    if (this.canChangePassword) {
      this.resetPassword = ResetPassword.fromUser(this.user!);
      this.passwordForm = this.resetPassword.getForm();
    }

    this.encounterForm = this.fb.group({
      enablePatientConsent: [false],
      consentMessage: [''],
      recorderFeedbackSound: [false]
    });
  }

  ngOnInit(): void {
    this.encounterForm.get('enablePatientConsent')?.valueChanges.subscribe((value) => {
      this.validateMessageRequired(value);
    });
    this.setEncounterForm();
  }

  togglePasswordVisibility(field: string): void {
    switch(field) {
      case 'current':
        this.showCurrentPassword = !this.showCurrentPassword;
        break;
      case 'new':
        this.showNewPassword = !this.showNewPassword;
        break;
      case 'confirm':
        this.showConfirmPassword = !this.showConfirmPassword;
        break;
    }
  }

  async changePassword(): Promise<void> {
    if (this.passwordForm.valid) {
      let message = this.translate.instant('Password changed successfully');
      try {
        this.isLoadingPassword = true;
        const formValue = this.passwordForm.value;
        await this.authService.updateUser({
          password: {
            current_password: formValue.currentPassword,
            new_password: formValue.newPassword,
            verify_password: formValue.newPassword,
          }
        });
        this.passwordForm.reset();
      } catch (error) {
        console.error('Failed to change password:', error);
        message = this.translate.instant('Unable to change password');
      } finally {
        this.isLoadingPassword = false;
      }
      this.snackBar.open(message, this.translate.instant('Close'), {
        duration: 2000,
        horizontalPosition: 'right',
        verticalPosition: 'bottom'
      });
    }
  }

  cancel(): void {
    this.passwordForm.reset();
  }

  private setEncounterForm(): void {
    if (!this.user) return;
    const userSettings = this.user.settings;
    const entitySettings = this.user.entity?.settings;
    const centrallyManaged = this.isConsentCentrallyManaged;

    this.encounterForm.reset({
      enablePatientConsent: centrallyManaged ? true : (userSettings.enableCopilotPatientConsent ?? false),
      consentMessage: centrallyManaged
        ? (entitySettings?.copilotPatientConsentMessage ?? '')
        : (userSettings.copilotPatientConsentMessage ?? ''),
      recorderFeedbackSound: userSettings.recorderFeedbackSound ?? false,
    });
    this.applyConsentLock(centrallyManaged);
  }

  private applyConsentLock(locked: boolean): void {
    const consentToggle = this.encounterForm.get('enablePatientConsent');
    const consentMessage = this.encounterForm.get('consentMessage');
    if (!consentToggle || !consentMessage) return;

    if (locked) {
      consentToggle.disable({ emitEvent: false });
      consentMessage.disable({ emitEvent: false });
    } else {
      consentToggle.enable({ emitEvent: false });
      consentMessage.enable({ emitEvent: false });
    }
  }

  async saveEncounterSettings(): Promise<void> {
    if (!this.encounterForm.valid) {
      this.encounterForm.markAllAsTouched();
      return;
    }
    if (this.encounterForm.pristine) return;
    const formValue = this.encounterForm.value;
    const settings: {
      recorderFeedbackSound: boolean;
      enableCopilotPatientConsent?: boolean;
      copilotPatientConsentMessage?: string;
    } = {
      recorderFeedbackSound: formValue.recorderFeedbackSound,
    };
    if (!this.isConsentCentrallyManaged) {
      settings.enableCopilotPatientConsent = formValue.enablePatientConsent;
      settings.copilotPatientConsentMessage = formValue.consentMessage;
    }
    let message = this.translate.instant('Encounter preferences saved successfully');
    try {
      this.isLoadingEncounter = true;
      this.user = await this.authService.updateUser({ settings });
      this.setEncounterForm();
    } catch (e) {
      console.error('Failed to save encounter preferences:', e);
      message = this.translate.instant('Unable to save encounter preferences');
    } finally {
      this.isLoadingEncounter = false;
      this.snackBar.open(message, this.translate.instant('Close'), {
        duration: 2000,
        horizontalPosition: 'right',
        verticalPosition: 'bottom'
      });
    }
  }

  validateMessageRequired(value: boolean): void {
    const consentControl = this.encounterForm.get('consentMessage');
    if (!consentControl) return;
    if (value) {
      consentControl.setValidators([Validators.required, Validators.maxLength(150)]);
    } else {
      consentControl.clearValidators();
      consentControl.setErrors(null);
    }
    consentControl.updateValueAndValidity({ emitEvent: false });
  }

  cancelEncounter(): void {
    this.setEncounterForm();
  }
}
